/**
 * Shared Media Handler service.
 *
 * ONE library service powers both the admin console and the customer (owner)
 * portal, so a single Media Library component can serve either side:
 *
 *   admin  → GET    /admin/media          (admin-only library)
 *   owner  → GET    /uploads/library      (owner-accessible library)
 *   delete → DELETE /admin/media          (admin — any file)
 *            DELETE /uploads/library      (owner — own uploads only, checked server-side)
 *
 * When the API is not configured the bundled demo library is used so the
 * experience stays explorable in preview mode.
 */

import { apiGetAuth, apiDeleteAuth, isApiConfigured } from "./http";
import { demoAdminMedia } from "@/mocks/admin";

/** Which side of the product is asking for the library. */
export type MediaScope = "admin" | "owner";

/** A single stored file in the media library. */
export interface MediaItem {
  name: string;
  folder: string;
  url: string;
  size: number;
  /** "image" | "font" | "file" */
  type: "image" | "font" | "file";
  modifiedAt: string;
  /** True when the signed-in user uploaded this file (owners may only delete these). */
  owned?: boolean;
}

const delay = (ms = 320): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Demo library — reused by both sides in preview mode. */
function demoMedia(folder?: string): MediaItem[] {
  const list = demoAdminMedia as MediaItem[];
  return folder ? list.filter((item) => item.folder === folder) : list;
}

/**
 * List the files in the media library for the given side.
 * Owners only ever read; deleting is reserved for admins.
 */
export async function listMedia(
  token: string | null,
  scope: MediaScope,
  folder?: string,
): Promise<MediaItem[]> {
  const query = folder ? `?folder=${encodeURIComponent(folder)}` : "";
  if (isApiConfigured() && token) {
    const path = scope === "admin" ? `/admin/media${query}` : `/uploads/library${query}`;
    return apiGetAuth<MediaItem[]>(path, token);
  }
  await delay();
  return demoMedia(folder);
}

/** Delete a single library file. Admins may remove any file; owners only their own. */
export async function deleteMedia(
  token: string | null,
  scope: MediaScope,
  folder: string,
  name: string,
): Promise<void> {
  if (isApiConfigured() && token) {
    const query = `?folder=${encodeURIComponent(folder)}&name=${encodeURIComponent(name)}`;
    const path = scope === "admin" ? `/admin/media${query}` : `/uploads/library${query}`;
    await apiDeleteAuth(path, token);
    return;
  }
  await delay(320);
}